/**
 * UTM Parameter Tracking Utility
 * Handles capturing, persisting, and retrieving UTM parameters for marketing attribution
 */

export interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  captured_at?: string
  landing_page?: string
  [key: `utm_${string}`]: string | undefined
}

const UTM_STORAGE_KEY = 'mx_utm_params'
const VALID_LANDING_PAGES = ['/shopping-cart', '/wishlist']

/**
 * Extract UTM parameters from URL search params
 */
export function extractUTMParams(
  searchParams: URLSearchParams,
): UTMParams | null {
  const utmParams: UTMParams = {}

  searchParams.forEach((value, key) => {
    if (key.startsWith('utm_') && value) {
      utmParams[key as `utm_${string}`] = value
    }
  })

  return Object.keys(utmParams).length > 0 ? utmParams : null
}

/**
 * Capture and store UTM parameters in sessionStorage
 * Only captures on valid landing pages (cart/wishlist)
 */
export function captureUTMParams(
  searchParams: URLSearchParams,
  currentPath: string,
): UTMParams | null {
  // Check if running in browser
  if (typeof window === 'undefined') {
    return null
  }

  // Only capture on cart/wishlist pages
  if (!VALID_LANDING_PAGES.some((page) => currentPath.includes(page))) {
    return null
  }

  const utmParams = extractUTMParams(searchParams)

  if (utmParams) {
    const enrichedParams: UTMParams = {
      ...utmParams,
      captured_at: new Date().toISOString(),
      landing_page: currentPath,
    }

    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(enrichedParams))
      return enrichedParams
    } catch (error) {
      console.error('Failed to store UTM parameters:', error)
      return null
    }
  }

  return null
}

/**
 * Retrieve stored UTM parameters from sessionStorage
 */
export function getStoredUTMParams(): UTMParams | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.error('Failed to retrieve UTM parameters:', error)
    return null
  }
}

/**
 * Clear stored UTM parameters
 * Should be called when user navigates away from checkout flow
 */
export function clearUTMParams(): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear UTM parameters:', error)
  }
}

/**
 * Check if current page is part of the conversion flow
 * (cart, wishlist, or checkout pages)
 */
export function isConversionFlowPage(pathname: string): boolean {
  const conversionPages = ['/shopping-cart', '/wishlist', '/checkout']
  return conversionPages.some((page) => pathname.includes(page))
}

/**
 * Format UTM params for GA4 event
 */
export function formatUTMForGA4(
  utmParams: UTMParams | null,
): Record<string, string> {
  if (!utmParams) return {}

  return {
    ...(utmParams.utm_source && { utm_source: utmParams.utm_source }),
    ...(utmParams.utm_medium && { utm_medium: utmParams.utm_medium }),
    ...(utmParams.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
    ...(utmParams.utm_content && { utm_content: utmParams.utm_content }),
    ...(utmParams.utm_term && { utm_term: utmParams.utm_term }),
  }
}
